import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicNps() {
  const { jobToken } = useParams();
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/nps/${jobToken}`).then((r) => {
      setData(r.data);
      if (r.data.already_rated && r.data.existing) {
        setScore(r.data.existing.score);
        setComment(r.data.existing.comment || "");
        setSubmitted(true);
      }
    }).catch(() => toast.error("Invalid link"));
  }, [jobToken]);

  const submit = async () => {
    if (score == null) return toast.error("Pick a score");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/nps/${jobToken}`, { job_token: jobToken, score, comment, reviewer_name: name });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (e) { toast.error("Failed to submit"); }
  };

  if (!data) return <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 flex items-center justify-center p-6" data-testid="public-nps-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <Star className="w-12 h-12 mx-auto text-amber-400" />
          <h1 className="font-display text-2xl font-semibold mt-2">{data.job.customer_name},</h1>
          <p className="text-sm text-slate-400 mt-1">How likely are you to recommend us to a friend or colleague?</p>
        </div>
        {submitted ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center" data-testid="nps-thanks">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <div className="font-semibold text-emerald-300">Thank you, {data.job.customer_name}!</div>
            <div className="text-xs text-slate-400 mt-1">You rated us {score}/10</div>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-[#0B1120] p-6 space-y-4">
            <div className="grid grid-cols-11 gap-1">
              {Array.from({ length: 11 }).map((_, i) => (
                <button
                  key={i}
                  data-testid={`score-${i}`}
                  onClick={() => setScore(i)}
                  className={`rounded-lg border h-10 text-sm font-bold transition-all ${score === i ? "border-amber-500/50 bg-amber-500/20 text-amber-200 scale-110" : "border-slate-800 bg-slate-900/40 text-slate-400 hover:border-amber-500/30"}`}
                >{i}</button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500">
              <span>Not likely</span><span>Very likely</span>
            </div>
            {score != null && (
              <>
                <Input data-testid="reviewer-name" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#030712] border-slate-800" />
                <Textarea data-testid="reviewer-comment" placeholder="Tell us what went well or what we can improve (optional)" value={comment} onChange={(e) => setComment(e.target.value)} className="bg-[#030712] border-slate-800 min-h-[80px]" />
                <Button data-testid="nps-submit" onClick={submit} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12">
                  Submit Rating
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
